import React, { useLayoutEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { calculatorTourSteps } from './calculatorTour';

const DIM_SOFT = 'rgba(15, 23, 42, 0.28)';
const DIM_STRONG = 'rgba(15, 23, 42, 0.62)';
const SPOTLIGHT_GREEN = '#39FF14';
const SPOTLIGHT_PAD = 8;
const cutoutRing = (dim) =>
  `0 0 0 3px ${SPOTLIGHT_GREEN}, 0 0 16px ${SPOTLIGHT_GREEN}, 0 0 0 9999px ${dim}`;

const cardPositionSx = (align) => {
  if (align === 'bottom-left') return { bottom: 20, left: 20, top: 'auto', right: 'auto' };
  if (align === 'bottom-right') return { bottom: 20, right: 20, top: 'auto', left: 'auto' };
  if (align === 'top-left') return { top: 20, left: 20, right: 'auto', bottom: 'auto' };
  return { top: 20, right: 20, left: 'auto', bottom: 'auto' };
};

const measureNode = (node) => {
  if (!node || typeof node.getBoundingClientRect !== 'function') return null;
  const next = node.getBoundingClientRect();
  if (!next.width || !next.height) return null;
  return {
    top: next.top - SPOTLIGHT_PAD,
    left: next.left - SPOTLIGHT_PAD,
    width: next.width + SPOTLIGHT_PAD * 2,
    height: next.height + SPOTLIGHT_PAD * 2,
  };
};

const findTourTarget = (spotlight) => {
  if (!spotlight || typeof document === 'undefined') return null;
  return document.querySelector(`[data-calculator-tour="${spotlight}"]`);
};

const OpportunityCalculatorTour = ({
  steps = calculatorTourSteps(),
  step = 0,
  onStepChange,
  onDismiss,
  layoutKey,
  testId = 'calculator-tour',
}) => {
  const safeStep = Math.min(Math.max(step, 0), Math.max(steps.length - 1, 0));
  const current = steps[safeStep] || {};
  const isFirst = safeStep === 0;
  const isLast = safeStep === steps.length - 1;
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    const target = findTourTarget(current.spotlight);
    if (!target) {
      setRect(null);
      return undefined;
    }

    const measure = () => setRect(measureNode(target));
    measure();
    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      measure();
    }

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    resizeObserver?.observe(target);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    const retry = window.setTimeout(measure, 350);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      window.clearTimeout(retry);
    };
  }, [current.spotlight, safeStep, layoutKey]);

  const hasCutout = Boolean(rect);
  const dim = hasCutout ? DIM_STRONG : DIM_SOFT;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
      data-testid={testId}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: hasCutout ? 'transparent' : dim,
        }}
      />
      {hasCutout && (
        <Box
          aria-hidden
          data-testid={`${testId}-spotlight`}
          sx={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: 2,
            boxShadow: cutoutRing(dim),
            pointerEvents: 'none',
          }}
        />
      )}

      <Paper
        role="dialog"
        aria-labelledby={`${testId}-title`}
        aria-describedby={`${testId}-body`}
        elevation={8}
        sx={{
          pointerEvents: 'auto',
          position: 'absolute',
          ...cardPositionSx(current.cardAlign),
          width: { xs: 'calc(100% - 40px)', sm: 360 },
          maxWidth: 360,
          p: 2,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {safeStep + 1} of {steps.length}
            </Typography>
            <Typography id={`${testId}-title`} variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {current.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onDismiss} aria-label="Skip tour">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography id={`${testId}-body`} variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {current.body}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Button size="small" onClick={onDismiss} sx={{ textTransform: 'none' }}>
            Skip
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              disabled={isFirst}
              onClick={() => onStepChange(safeStep - 1)}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() => (isLast ? onDismiss() : onStepChange(safeStep + 1))}
              sx={{ textTransform: 'none' }}
            >
              {isLast ? 'Done' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default OpportunityCalculatorTour;
